import React, { useState, useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import CloudWatchLogs, {
  FilterLogEventsRequest
} from 'aws-sdk/clients/cloudwatchlogs';
import LogStreamTable, { LogRow, log } from '../logs/LogStreamTable';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import moment, { Moment } from 'moment';
import axiosConfig, { api } from '../../utils/axiosConfig';
import { Notification } from '../../interfaces/api/Notifications';
import Input from 'antd/lib/input';
import {
  Alert,
  Button,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Tabs
} from 'antd';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import { setAWSCreds } from '../../utils/accessToken';
import { Environments } from '../../api/environments/interface';
import { LogsRetentionGETResponse } from '../../interfaces/backend/LogsRetentionGETResponse';
import { tz } from 'moment-timezone';
import {
  NotificationDetailsStatus,
  NotificationSummaryLogMessage
} from '../../interfaces/api/Logs';
import { Channels } from '../common/channelsEnum';
import { getChannelReadableName } from '../../utils/readable';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import Paragraph from 'antd/es/typography/Paragraph';
import LogDetails from './LogDetails';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import Loading from '../loading';
import styled from 'styled-components';
import { AWSError } from 'aws-sdk';
import { unparse } from 'papaparse';
import { saveAs } from 'file-saver';
import { readWebsiteVariable } from '../../utils/readWebsiteVariable';
import axios from 'axios';
import JsonDisplay from './JsonDisplay';
import { green, red } from '@ant-design/colors';

const CustomCollapsePanel = styled(Collapse.Panel)`
  & .ant-collapse-header {
    padding-left: 0 !important;
  }

  & .ant-collapse-content-box {
    padding-left: 0 !important;
  }
`;

// here, we are creating a moment compatible datepicker
// we rely on moment (antdv4), but antdv5 uses dayjs for its datepicker
// https://ant.design/docs/react/use-custom-date-library
const MomentDatePicker =
  DatePicker.generatePicker<Moment>(momentGenerateConfig);

const Logs = (): ReactElement => {
  const { accountId } = useParams<{ accountId: string }>();
  const [envs, setEnvs] = useState<Environments[]>();

  // load default filters from URL:
  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const query = useQuery();
  const defaultDateRange: [Moment, Moment] | undefined =
    query.get('start') && query.get('end')
      ? [
          moment.unix(Number.parseInt(query.get('start') || '')),
          moment.unix(Number.parseInt(query.get('end') || ''))
        ]
      : undefined;
  const defaultNotificationFilter: string[] | undefined = query.get(
    'notifications'
  )
    ? query.get('notifications')?.split(',')
    : [];
  const defaultUserFilter: string = query.get('user') || '';
  const defaultChannelFilter: string[] | undefined = query.get('channel')
    ? query.get('channel')?.split(',')
    : [];
  const defaultStatusFilter: string | undefined =
    query.get('status') || undefined;
  const defaultTrackingIdFilter: string = query.get('trackingId') || '';
  const defaultCustomFilter: string = query.get('custom') || '';
  const defaultEnvIdFilter: string = query.get('envId') || '';
  const defaultOpenedLogTrackingId = query.get('openedLog');

  // filters:
  const [dateRangeFilter, setDateRangeFilter] =
    useState<[Moment, Moment] | undefined>(defaultDateRange);
  const [notificationFilter, setNotificationFilter] = useState<
    string[] | undefined
  >(defaultNotificationFilter);
  const [userFilter, setUserFilter] = useState<string>(defaultUserFilter);
  const [channelFilter, setChannelFilter] =
    useState<string[] | undefined>(defaultChannelFilter);
  const [statusFilter, setStatusFilter] =
    useState<string | undefined>(defaultStatusFilter);
  const [trackingIdFilter, setTrackingIdFilter] = useState<string>(
    defaultTrackingIdFilter
  );
  const [customFilter, setCustomFilter] = useState<string>(defaultCustomFilter);
  const [envIdFilter, setEnvIdFilter] = useState(defaultEnvIdFilter);

  // other states:
  const [timezone, setTimezone] = useState(tz.guess());
  const [logsData, setLogsData] = useState<LogRow[]>();
  const [notifications, setNotifications] = useState<Notification[]>();
  const [retetionInDays, setRetentionInDays] = useState(7);
  const [moreFiltersIsOpen] = useState(
    envIdFilter ||
      customFilter ||
      trackingIdFilter ||
      (channelFilter && channelFilter.length > 0) ||
      timezone !== tz.guess()
  );
  const [openedLogTrackingId, setOpenedLogTrackingId] = useState<
    string | undefined
  >(defaultOpenedLogTrackingId ?? undefined);
  const [openedLogLogs, setOpenedLogLogs] = useState<log[]>();
  const [searching, setSearching] = useState(false);
  const lastSearchTimesstampRef = useRef(new Date().getTime());

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // user wants to resend these items:
  const [resending, setResending] = useState<
    {
      trackingId: string;
      envId: string;
      request: string;
      response: string | undefined;
      error: unknown;
    }[]
  >([]);
  // resent is running right now
  const [resendRunning, setResendRunning] = useState(false);
  // resending has completed
  const [resendRan, setResendRan] = useState(false);

  // save changes to URL:
  const history = useHistory();
  useEffect(() => {
    if (!dateRangeFilter) return;

    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (dateRangeFilter) {
      params.set('start', dateRangeFilter[0].unix().toString());
      params.set('end', dateRangeFilter[1].unix().toString());
    }
    if (notificationFilter && notificationFilter.length > 0)
      params.set('notifications', notificationFilter.join(','));
    if (channelFilter && channelFilter.length > 0)
      params.set('channel', channelFilter.join(','));
    if (userFilter) params.set('user', userFilter);
    if (trackingIdFilter) params.set('trackingId', trackingIdFilter);
    if (customFilter) params.set('custom', customFilter);
    if (envIdFilter) params.set('envId', envIdFilter);
    if (openedLogTrackingId) params.set('openedLog', openedLogTrackingId);

    history.push({
      search: params.toString()
    });
  }, [
    query,
    history,
    statusFilter,
    dateRangeFilter,
    notificationFilter,
    channelFilter,
    userFilter,
    trackingIdFilter,
    customFilter,
    envIdFilter,
    timezone,
    openedLogTrackingId
  ]);

  // initial load:
  useEffect(() => {
    api('GET', `${accountId}/logs/retention`).then((res) => {
      setRetentionInDays(
        (res.data as LogsRetentionGETResponse).retentionInDays ?? 3653 // undefined => indefinte (10 years)
      );
    });

    axiosConfig('get', `${accountId}/notifications`, {}, (response) => {
      const res = response as Record<'data', Notification[]>;
      setNotifications(res.data);
    });

    axiosConfig('get', `${accountId}/environments`, {}, (response) => {
      const res = response as Record<'data', Environments[]>;
      setEnvs(res.data);
    });
  }, [accountId]);

  // set the default date range to the last 100 logs (if url has not set it yet)
  useEffect(() => {
    // the user may click the log button in the menu again, to reload the page with
    // new logs. in this case, we want to reset the date range filter.
    // but react-router doesn't do anything because this component is already loaded.
    // this code is to reload it ourselves
    if (dateRangeFilter && !query.get('start')) {
      setDateRangeFilter(undefined);
      query.delete('start');
      query.delete('');
      return;
    }

    if (dateRangeFilter) return;
    setAWSCreds().then(async () => {
      const cloudwatch = new CloudWatchLogs();
      const res = await cloudwatch
        .describeLogStreams({
          logGroupName: `EnvLogs/${accountId}`,
          orderBy: 'LastEventTime',
          descending: true,
          limit: 50
        })
        .promise();

      // loop through res.logStreams and find the min based on creationTime and max based on lastEventTimestamp
      let max: Moment | undefined;
      let min: Moment | undefined;
      res.logStreams?.forEach((logStream) => {
        if (logStream.logStreamName === 'NotificationsSummary') return;
        const lastEventTimestamp = moment(logStream.lastIngestionTime);
        const creationTime = moment(logStream.creationTime);
        if (!max || lastEventTimestamp.isAfter(max)) max = lastEventTimestamp;
        if (!min || creationTime.isBefore(min)) min = creationTime;
      });

      let start: Moment;
      let end: Moment;
      if (max && min) {
        const diff = max.diff(min, 'seconds');
        if (diff < 60) {
          start = moment(max).subtract(5, 'minutes');
          end = moment(max);
        } else if (diff < 900) {
          start = moment(max).subtract(15, 'minutes');
          end = moment(max);
        } else if (diff < 1800) {
          start = moment(max).subtract(30, 'minutes');
          end = moment(max);
        } else if (diff < 3600) {
          start = moment(max).subtract(1, 'hour');
          end = moment(max);
        } else if (diff < 3600 * 12) {
          start = moment(max).subtract(12, 'hour');
          end = moment(max);
        } else if (diff < 86400) {
          start = moment(max).subtract(1, 'day');
          end = moment(max);
        } else {
          start = moment(min).startOf('day');
          end = moment(max).endOf('day');
        }
      } else {
        start = moment().subtract(1, 'hour');
        end = moment();
      }

      setDateRangeFilter([start, end]);
    });
  }, [accountId, dateRangeFilter, query]);

  // querying:
  useEffect(() => {
    if (!dateRangeFilter) return;

    setLogsData(undefined);
    const groupedLogsByTrackingId: Record<string, LogRow[]> = {};
    const searchTimestamp = new Date().getTime();
    lastSearchTimesstampRef.current = searchTimestamp;

    setSearching(true);
    setAWSCreds().then(async () => {
      let nextToken: string | undefined;
      do {
        const result = await getLogSummary(
          accountId,
          dateRangeFilter,
          notificationFilter ?? [],
          channelFilter ?? [],
          userFilter,
          statusFilter ?? '',
          trackingIdFilter,
          customFilter,
          envIdFilter,
          nextToken
        );
        nextToken = result.nextToken;
        // Grouping all logs with same tracking Ids

        result.events.forEach((event): void => {
          const message: NotificationSummaryLogMessage = JSON.parse(
            event.message as string
          );
          if (!groupedLogsByTrackingId[message.trackingId]) {
            groupedLogsByTrackingId[message.trackingId] = [];
          }
          groupedLogsByTrackingId[message.trackingId].push({
            trackingId: message.trackingId,
            user: message.request?.user ?? {}, // some old logs don't have the request obj
            notificationId: message.request?.notificationId ?? '',
            channels: message.channels,
            status: message.status,
            timestamp: event.timestamp as number,
            request: JSON.stringify(message.request),
            envId: message.envId
          });
        });
        //Sort logs based on the timeStamps
        const sortedLogs: Record<string, LogRow[]> = Object.fromEntries(
          Object.entries(groupedLogsByTrackingId).map(([trackingId, rows]) => {
            const sortedRows = [...rows].sort(
              (a, b) => a.timestamp - b.timestamp
            );
            return [trackingId, sortedRows];
          })
        );

        // filter out logs without a received event
        // Some filters do not contain RECEIVED
        const filteredLogs: Record<string, LogRow[]> = Object.fromEntries(
          Object.entries(sortedLogs).filter(([, rows]) => {
            if (
              channelFilter ||
              (statusFilter && statusFilter !== '') ||
              customFilter
            ) {
              return true;
            } else {
              return rows.some(
                (item) => item.status === NotificationDetailsStatus.RECEIVED
              );
            }
          })
        );

        // create uniqueEvents array from allLogs where the timestamp is the earliest of the merged events and state is the latest state:
        const uniqueLogs = Object.values(filteredLogs).map((rows) => ({
          trackingId: rows[0].trackingId,
          user: rows[0].user,
          status: rows[rows.length - 1].status, // store the latest status
          notificationId: rows[0].notificationId,
          channels: rows[0].channels,
          timestamp: rows[0].timestamp, // store the oldest timestamp
          request: rows[0].request,
          envId: rows[0].envId
        }));
        if (lastSearchTimesstampRef.current !== searchTimestamp) {
          // another search has started, stop this loop
          break;
        }
        setLogsData(uniqueLogs.reverse());
      } while (
        nextToken &&
        lastSearchTimesstampRef.current === searchTimestamp // this is still the last search
      );
      if (lastSearchTimesstampRef.current === searchTimestamp) {
        // the last search has ended:
        setSearching(false);
      }
    });
  }, [
    accountId,
    dateRangeFilter,
    notificationFilter,
    channelFilter,
    userFilter,
    statusFilter,
    trackingIdFilter,
    customFilter,
    envIdFilter
  ]);

  // if there is an openedLog in the URL, load it:
  useEffect(() => {
    if (!openedLogTrackingId) {
      setOpenedLogLogs(undefined);
    } else {
      getLogDetailsByAccountId(accountId, openedLogTrackingId).then((res) => {
        setOpenedLogLogs(res as log[]);
      });
    }
  }, [openedLogTrackingId, accountId]);

  if (!notifications) return <Loading />;

  return (
    <div
      style={{
        padding: '20px',
        paddingBottom: '120px'
      }}
    >
      <Row>
        <Col
          style={{
            width: 550,
            paddingTop: 14,
            paddingBottom: 16,
            marginRight: 20
          }}
        >
          <Row gutter={[20, 20]}>
            {/* Date Range */}
            <Col span={24}>
              <Paragraph
                strong
                style={{
                  marginBottom: 14
                }}
              >
                Filters
              </Paragraph>
              <Row align="middle">
                <Col style={{ width: 100 }}>
                  <Text>Date: </Text>
                </Col>
                <Col span="auto">
                  <MomentDatePicker.RangePicker
                    style={{
                      width: 360
                    }}
                    allowClear={false}
                    allowEmpty={[false, false]}
                    showTime
                    value={dateRangeFilter}
                    onChange={(values) => {
                      if (values) {
                        setDateRangeFilter([
                          moment(values[0]),
                          moment(values[1])
                        ]);
                      }
                    }}
                    ranges={{
                      '5 Minutes': [moment().subtract(5, 'minutes'), moment()],
                      '15 Minutes': [
                        moment().subtract(15, 'minutes'),
                        moment()
                      ],
                      '30 Minutes': [
                        moment().subtract(30, 'minutes'),
                        moment()
                      ],
                      '1 Hour': [moment().subtract(1, 'hours'), moment()],
                      '12 Hours': [moment().subtract(12, 'hours'), moment()],
                      '1 Day': [moment().subtract(1, 'days'), moment()],
                      '7 Days': [moment().subtract(7, 'days'), moment()],
                      '30 Days': [moment().subtract(30, 'days'), moment()]
                    }}
                    disabledDate={(current) => {
                      return (
                        current <
                          moment()
                            .add(-retetionInDays - 1, 'days')
                            .endOf('day') || current > moment().endOf('day')
                      );
                    }}
                  />
                </Col>
                <Col style={{ width: 80 }}>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {/* display the diff of daterange in minutes, hours or days */}
                    {dateRangeFilter &&
                      (dateRangeFilter[1].diff(dateRangeFilter[0], 'minutes') <
                      60
                        ? dateRangeFilter[1].diff(
                            dateRangeFilter[0],
                            'minutes'
                          ) + ' min'
                        : dateRangeFilter[1].diff(dateRangeFilter[0], 'hours') <
                          24
                        ? dateRangeFilter[1].diff(dateRangeFilter[0], 'hours') +
                          ' hr'
                        : dateRangeFilter[1].diff(dateRangeFilter[0], 'days') +
                          ' d')}
                  </Text>
                </Col>
              </Row>
            </Col>
            {/* Status */}
            <Col span={24}>
              <Row align="middle">
                <Col style={{ width: 100 }}>
                  <Text>Status: </Text>
                </Col>
                <Col>
                  <Select
                    allowClear={true}
                    showSearch={true}
                    style={{ width: 360 }}
                    onChange={(value) => {
                      setStatusFilter(value);
                    }}
                    defaultValue={statusFilter}
                    options={[
                      {
                        value: 'SUCCESS',
                        label: 'Successful'
                      },
                      {
                        value: 'FAILURE',
                        label: 'Failed'
                      },
                      {
                        value: '',
                        label: 'All'
                      }
                    ]}
                  />
                </Col>
              </Row>
            </Col>
            {/* User */}
            <Col span={24}>
              <Row align="middle">
                <Col style={{ width: 100 }}>
                  <Text>User: </Text>
                </Col>
                <Col>
                  <Input.Search
                    placeholder="id, email or number"
                    defaultValue={userFilter}
                    onSearch={(value) => setUserFilter(value.trim())}
                    style={{
                      width: 360
                    }}
                  />
                </Col>
              </Row>
            </Col>
            {/* Notifications */}
            <Col span={24}>
              <Row align="middle">
                <Col style={{ width: 100 }}>
                  <Text>Notification: </Text>
                </Col>
                <Col>
                  <Select
                    allowClear={true}
                    mode="multiple"
                    showSearch={true}
                    style={{ width: 360 }}
                    defaultValue={notificationFilter}
                    onChange={(value) => {
                      setNotificationFilter(value);
                    }}
                    filterOption={(input, option) =>
                      option?.label
                        ? option.label
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0 ||
                          option.value
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        : false
                    }
                    options={[
                      ...notifications.map((n) => {
                        return {
                          value: n.notificationId,
                          label:
                            n.title +
                            (n.title !== n.notificationId
                              ? ` (${n.notificationId})`
                              : '')
                        };
                      })
                    ]}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col style={{ width: 550 }}>
          <Collapse
            bordered={false}
            style={{ background: 'none' }}
            defaultActiveKey={moreFiltersIsOpen ? '1' : undefined}
          >
            <CustomCollapsePanel header="More Filters" key="1">
              <Row gutter={[20, 20]}>
                <Col span={24}>
                  <Row align="middle">
                    <Col style={{ width: 100 }}>
                      <Text>Channel: </Text>
                    </Col>
                    <Col>
                      <Select
                        allowClear={true}
                        mode="multiple"
                        showSearch={true}
                        style={{ width: 360 }}
                        value={channelFilter}
                        onChange={(value) => {
                          setChannelFilter(value);
                        }}
                        filterOption={(input, option) =>
                          option?.label
                            ? option.label
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0 ||
                              option.value
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            : false
                        }
                        options={Object.keys(Channels).map((c) => {
                          return {
                            value: c,
                            label: getChannelReadableName(c)
                          };
                        })}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row align="middle">
                    <Col style={{ width: 100 }}>
                      <Text>TrackingId: </Text>
                    </Col>
                    <Col>
                      <Input.Search
                        defaultValue={defaultTrackingIdFilter}
                        onSearch={(value) => {
                          if (value.trim()) setTrackingIdFilter(value.trim());
                          else setTrackingIdFilter('');
                        }}
                        style={{
                          width: 360
                        }}
                      />
                    </Col>
                  </Row>
                </Col>

                {/* Custom */}
                <Col span={24}>
                  <Row align="middle">
                    <Col style={{ width: 100 }}>
                      <Text>Custom Query: </Text>
                    </Col>
                    <Col>
                      <Input.Search
                        style={{
                          width: '360px'
                        }}
                        onSearch={(input) => {
                          setCustomFilter(input);
                        }}
                        defaultValue={defaultCustomFilter}
                      />
                    </Col>
                    <Col span="auto">
                      <Popover
                        overlayStyle={{ maxWidth: '400px' }}
                        content={
                          <>
                            <Row>
                              <Title level={4}>Query Examples:</Title>
                            </Row>
                            <Row style={{ marginBottom: '8px' }}>
                              Search the mergetags:
                              <Text code copyable>
                                $.request.mergeTags.firstName="jane"
                              </Text>
                            </Row>
                            <Row style={{ marginBottom: '16px' }}>
                              Operators:
                              <Text code copyable>
                                (Condition_A && (Condition_B || Condition_C))
                              </Text>
                            </Row>
                            <Row>
                              <Text type="warning">
                                Queries will combine with the filters on the
                                table.
                              </Text>
                            </Row>
                          </>
                        }
                      >
                        <InfoCircleOutlined
                          style={{
                            marginLeft: '8px'
                          }}
                        />
                      </Popover>
                    </Col>
                  </Row>
                </Col>

                {/* EnvId */}
                {envs && envs.length > 1 && (
                  <Col span={24}>
                    <Row align="middle">
                      <Col style={{ width: 100 }}>
                        <Text>Environment: </Text>
                      </Col>
                      <Col span="auto">
                        <Select
                          defaultValue={defaultEnvIdFilter}
                          onChange={(value) => {
                            setEnvIdFilter(value);
                          }}
                          options={[
                            { value: '', label: 'Any Environment' },
                            ...envs.map((e) => {
                              return {
                                value: e.clientId,
                                label:
                                  e.title.endsWith(
                                    'notificationAPITestEnvironment'
                                  ) ||
                                  e.title.endsWith('+Testing') ||
                                  e.title.endsWith(',Testing')
                                    ? 'Testing'
                                    : 'Production'
                              };
                            })
                          ]}
                          style={{ width: 360 }}
                        />
                      </Col>
                    </Row>
                  </Col>
                )}

                {/* Timezone */}
                <Col span={24}>
                  <Row align="middle">
                    <Col style={{ width: 100 }}>
                      <Text>Timezone: </Text>
                    </Col>
                    <Col span="auto">
                      <Select
                        defaultValue={timezone}
                        showSearch={true}
                        bordered={false}
                        style={{ width: 360 }}
                        onChange={(value) => {
                          setTimezone(value);
                        }}
                        // search the label, not the value
                        filterOption={(input, option) =>
                          option?.label
                            ? option.label
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            : false
                        }
                        options={[
                          { value: 'local', label: 'Local' },
                          ...moment.tz.names().map((tz) => {
                            return {
                              value: tz,
                              // add the timezone offset to the label
                              label: `(${moment.tz(tz).format('Z')}${
                                moment.tz(tz).format('z').startsWith('+') ||
                                moment.tz(tz).format('z').startsWith('-')
                                  ? ''
                                  : ' ' + moment.tz(tz).format('z')
                              }) ${tz}`
                            };
                          })
                        ]}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CustomCollapsePanel>
          </Collapse>
        </Col>
      </Row>

      <Divider />
      <Row
        style={{
          marginBottom: '10px'
        }}
      >
        <Col span={12}>
          <Text type="secondary">
            {logsData?.length
              ? `${logsData?.length} logs found between ${dateRangeFilter?.[0]
                  .tz(timezone)
                  ?.format('YYYY-MM-DD HH:mm:ss')} and ${dateRangeFilter?.[1]
                  .tz(timezone)
                  .format('YYYY-MM-DD HH:mm:ss')}`
              : ''}
          </Text>
        </Col>
        <Col span={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {logsData?.length ? (
            <>
              <Button
                type="text"
                onClick={() => {
                  setResending(
                    logsData
                      .filter((_l, i) => {
                        return (
                          i < pageSize * currentPage &&
                          i >= pageSize * (currentPage - 1)
                        );
                      })
                      .map((log) => {
                        return {
                          trackingId: log.trackingId,
                          envId: log.envId,
                          request: log.request,
                          response: undefined,
                          error: undefined
                        };
                      })
                  );
                }}
              >
                Resend Current Page
              </Button>
              <Button
                onClick={() => handleDownload(logsData, timezone)}
                type="text"
              >
                Export
              </Button>
            </>
          ) : null}
        </Col>
      </Row>
      <LogStreamTable
        logs={logsData || []}
        loading={searching}
        notifications={notifications || []}
        openLogDetails={setOpenedLogTrackingId}
        timezone={timezone}
        setResending={setResending}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
      />

      {openedLogTrackingId && (
        <LogDetails
          trackingId={openedLogTrackingId}
          detailedLogs={openedLogLogs}
          timezone={timezone}
          setOpenedLog={setOpenedLogTrackingId}
        />
      )}

      <Modal
        title={'Reseding'}
        open={resending.length > 0}
        onCancel={() => {
          setResending([]);
          setResendRan(false);
          setResendRunning(false);
        }}
        maskClosable={false}
        okText={
          resendRan
            ? 'Download results and close'
            : 'Resend ' +
              resending.length +
              ' notification' +
              (resending.length > 1 ? 's' : '')
        }
        onOk={() => {
          if (resendRan) {
            const csvData = resending.map((item) => {
              return {
                clientId: item.envId,
                'original trackingId': item.trackingId,
                request: item.request,
                response: JSON.stringify(item.response),
                error: JSON.stringify(item.error)
              };
            });
            const csv = unparse(csvData);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, 'resend results.csv');

            setResending([]);
            setResendRan(false);
          } else {
            if (!envs) {
              console.log('envs not found');
              return;
            }
            const host = readWebsiteVariable('REACT_APP_api_domain');
            setResendRunning(true);

            resending.forEach((r) => {
              const env = envs?.find((e) => e.clientId === r.envId);
              const url = `https://${host}/${env?.clientId}/sender`;
              axios
                .post(url, r.request, {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization:
                      'Basic ' +
                      Buffer.from(
                        `${env?.clientId}:${env?.clientSecret}`
                      ).toString('base64')
                  }
                })
                .then((response) => {
                  const newResending = [...resending];
                  const updatedItem = newResending.find(
                    (re) => re.trackingId === r.trackingId
                  );
                  if (updatedItem) updatedItem.response = response.data;
                  setResending(newResending);
                  if (resending.every((r) => r.response || r.error)) {
                    setResendRunning(false);
                    setResendRan(true);
                  }
                })
                .catch((error) => {
                  const newResending = [...resending];
                  const updatedItem = newResending.find(
                    (re) => re.trackingId === r.trackingId
                  );
                  if (updatedItem) updatedItem.error = error;
                  setResending(newResending);

                  if (resending.every((r) => r.response || r.error)) {
                    setResendRunning(false);
                    setResendRan(true);
                  }
                });
            });
          }
        }}
        okButtonProps={{
          disabled: resendRunning,
          loading: resendRunning
        }}
      >
        <Space direction="vertical" style={{ maxWidth: '100%' }}>
          <Text>
            Repeats the selected notification requests with the same data as the
            original request. The results are recorded as separate new log
            lines, visible after updating the date range filter.
          </Text>

          <Collapse>
            {resending.map((r) => (
              <Collapse.Panel
                header={r.trackingId}
                extra={
                  r.response ? (
                    <CheckCircleOutlined
                      style={{
                        color: green.primary
                      }}
                    />
                  ) : r.error ? (
                    <CloseCircleOutlined
                      style={{
                        color: red.primary
                      }}
                    />
                  ) : (
                    ''
                  )
                }
                key={r.trackingId}
              >
                <Tabs
                  defaultActiveKey={r.response ? 'response' : 'request'}
                  tabBarStyle={{
                    width: '100%'
                  }}
                >
                  <Tabs.TabPane tab="Request" key="request">
                    <JsonDisplay json={JSON.parse(r.request as string)} />
                  </Tabs.TabPane>
                  {r.response || r.error ? (
                    <Tabs.TabPane tab="Response" key="response">
                      <JsonDisplay
                        json={r.response ?? JSON.parse(JSON.stringify(r.error))}
                      />
                    </Tabs.TabPane>
                  ) : null}
                </Tabs>
              </Collapse.Panel>
            ))}
          </Collapse>
          {!resendRan && (
            <Alert
              message="This action cannot be undone!"
              type="error"
              showIcon
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};
const handleDownload = (data: LogRow[], timezone: string) => {
  const prepareCSVObject = (obj: LogRow, timezone: string) => {
    const {
      trackingId,
      user,
      status,
      notificationId,
      channels,
      timestamp,
      request
    } = obj;
    const { id: userId, email, number } = user;
    return {
      trackingId,
      userId,
      email,
      number,
      status,
      notificationId,
      channels,
      timestamp: moment(timestamp).tz(timezone).format('YYYY-MM-DD h:mm A'),
      request
    };
  };
  const csvData = data.map((item) => prepareCSVObject(item, timezone));
  const csv = unparse(csvData);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, 'NotificationAPI_logs_export.csv');
};
const getLogSummary = async (
  accountId: string,
  dateRangeFilter: [Moment, Moment],
  notificationFilter: string[],
  channelFilter: string[],
  userFilter: string,
  statusFilter: string,
  trackingId: string,
  customFilter: string,
  envIdFilter: string,
  nextToken?: string
): Promise<{
  events: CloudWatchLogs.FilteredLogEvents;
  nextToken?: string;
}> => {
  await setAWSCreds();
  const cloudwatchLogs = new CloudWatchLogs();

  const params: FilterLogEventsRequest = {
    logGroupName: `EnvLogs/${accountId}`,
    logStreamNames: ['NotificationsSummary'],
    startTime: dateRangeFilter[0].valueOf(),
    endTime: dateRangeFilter[1].valueOf()
  };

  const filterPatterns = [];
  if (notificationFilter && notificationFilter.length > 0) {
    const notificationPatterns = notificationFilter.map(
      (f) => `($.request.notificationId = "${f}")`
    );
    filterPatterns.push('(' + notificationPatterns.join(' || ') + ')');
  }

  if (channelFilter && channelFilter.length > 0) {
    const channelPatterns = channelFilter.map(
      (f) => `($.channelsObj.${f} is true)`
    );
    filterPatterns.push('(' + channelPatterns.join(' || ') + ')');
  }

  if (userFilter && userFilter.length > 0) {
    filterPatterns.push(
      `($.request.user.id = "*${userFilter}*" || $.request.user.email = "*${userFilter}*" || $.request.user.number = "*${userFilter}*")`
    );
  }

  if (statusFilter && statusFilter !== '') {
    const statusFilterPattern = `($.status = "${statusFilter}")`;
    filterPatterns.push('(' + statusFilterPattern + ')');
  }

  if (trackingId && trackingId !== '') {
    const trackingIdFilterPattern = `($.trackingId = "${trackingId}")`;
    filterPatterns.push('(' + trackingIdFilterPattern + ')');
  }

  if (customFilter) filterPatterns.push('(' + customFilter + ')');

  if (envIdFilter !== '') filterPatterns.push(`($.envId = "${envIdFilter}")`);

  if (filterPatterns.length > 0)
    params.filterPattern = '{' + filterPatterns.join(' && ') + '}';

  if (nextToken) params.nextToken = nextToken;

  try {
    const result = await cloudwatchLogs.filterLogEvents(params).promise();
    return { events: result.events ?? [], nextToken: result.nextToken };
  } catch (err) {
    const error = err as AWSError;
    if (error.code && error.code === 'ResourceNotFoundException') {
      return { events: [] };
    } else {
      throw error;
    }
  }
};

const getLogDetailsByAccountId = async (
  accountId: string,
  trackingId: string
): Promise<log[]> => {
  await setAWSCreds();
  const cloudwatchLogs = new CloudWatchLogs();

  const logs: log[] = [];
  const params: FilterLogEventsRequest = {
    logGroupName: `EnvLogs/${accountId}`,
    logStreamNames: [`Notification/${trackingId}`]
  };
  const res = await cloudwatchLogs.filterLogEvents(params).promise();
  res?.events?.forEach((event) => {
    logs.push({
      message: JSON.parse(event.message as string),
      timestamp: event.timestamp ?? 0
    });
  });
  return logs;
};

export default Logs;
